import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { giftThemes } from '../data/mockData';
import { giftingOccasions } from '../data/siteContent';
import { PageHero } from '../components/PageHero';
import { SectionHeading } from '../components/SectionHeading';
import { CardCarousel } from '../components/CardCarousel';
const EASE = [0.23, 1, 0.32, 1] as const;
export function Gifting() {
  return <div className="w-full bg-paper" data-id="element-432" data-mp="1cdy30wsi3h0a">
      <PageHero eyebrow="Gifting" title="A gift that gets used every day." intro="Not a candle that stays in the cupboard. Pick by theme for one person, or brand a run of cups for two hundred." data-id="element-433" data-mp="1cezev3hcc7j2">
        <div className="flex flex-wrap gap-3" data-id="element-434" data-mp="12nuzi2nr6knp">
          <a href="#personal" className="type-cta rounded-full bg-coffee px-8 py-4 text-[11px] text-paper transition-colors hover:bg-pennant" data-id="element-435" data-mp="2040qezlgzysk">
            Personal gifts
          </a>
          <a href="#occasions" className="type-cta rounded-full border border-coffee/25 px-8 py-4 text-[11px] text-coffee transition-colors hover:bg-coffee hover:text-paper" data-id="element-436" data-mp="2uoiztznribcl">
            Events &amp; occasions
          </a>
          <Link to="/corporate-branding" className="type-cta rounded-full border border-coffee/25 px-8 py-4 text-[11px] text-coffee transition-colors hover:bg-coffee hover:text-paper" data-id="element-437" data-mp="2pwrfi6v791uw">
            Corporate &amp; branded
          </Link>
        </div>
      </PageHero>

      {/* Personal gifting */}
      <section id="personal" className="mx-auto max-w-[1400px] scroll-mt-28 px-6 py-20 md:py-28" data-id="element-438" data-mp="21zu5qkl4j6ac">
        <SectionHeading eyebrow="Personal gifting" title="Start with who it’s for." subtitle="Each theme drops you into the shop with the right cups already filtered." data-id="element-439" data-mp="26yf5yk0dlpys" />
        <CardCarousel cols={3} className="mt-14" data-id="element-440" data-mp="55qzm1sfqmes">
          {giftThemes.map((g, i) => <motion.div key={g.id} initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.5,
          delay: i * 0.05,
          ease: EASE
        }} data-id="element-441" data-mp="2tf0rf5a4oln" data-mpi={i}>
              <Link to={`/shop?gift=${g.id}`} className="group block" data-id="element-442" data-mp="3kys0x1v5dxym">
                <div className="overflow-hidden rounded-4xl" data-id="element-443" data-mp="mgct20gi06zb">
                  <img src={g.image} alt={g.title} className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" data-id="element-444" data-mp="mhnu3sik70mq" />
                </div>
                <h3 className="type-display mt-5 text-2xl text-coffee" data-id="element-445" data-mp="7q5e3upp963d">
                  {g.title}
                </h3>
                <p className="type-body mt-1 text-sm text-ink/60" data-id="element-446" data-mp="158favf4vltb5">{g.blurb}</p>
                <span className="type-cta mt-3 inline-flex items-center gap-1.5 text-[10px] text-pennant" data-id="element-447" data-mp="10e1b70j6l00o">
                  Shop this
                  <ArrowRightIcon className="h-3 w-3 transition-transform duration-200 group-hover:translate-x-1" strokeWidth={1.5} data-id="element-448" data-mp="17t4xj5qysc6s" />
                </span>
              </Link>
            </motion.div>)}
        </CardCarousel>
      </section>

      {/* Events & occasions */}
      <section id="occasions" className="w-full scroll-mt-28 bg-paper-warm py-20 md:py-28" data-id="element-449" data-mp="1qqee8f4wnbkc">
        <div className="mx-auto max-w-[1400px] px-6" data-id="element-450" data-mp="1vneluuhekmdk">
          <SectionHeading eyebrow="Events & occasions" title="One cup per guest, instead of a thousand paper ones." data-id="element-451" data-mp="20fpoi53j2m9t" />
          <CardCarousel className="mt-14" data-id="element-452" data-mp="25dzkuj0znsx0">
            {giftingOccasions.map((o, i) => <motion.div key={o.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.45,
            delay: i * 0.05,
            ease: EASE
          }} className="flex h-full flex-col rounded-3xl border border-oat/70 bg-paper p-8" data-id="element-453" data-mp="2aex3ewpwuoz1" data-mpi={i}>
                <h3 className="type-display text-2xl text-coffee" data-id="element-454" data-mp="3kv41me6b515n">
                  {o.title}
                </h3>
                <p className="type-body mt-3 text-sm text-ink/65" data-id="element-455" data-mp="3gvlnq2cwlqcb">{o.body}</p>
                <Link to="/corporate-branding#brief" className="type-cta mt-auto pt-6 text-[10px] text-pennant" data-id="element-456" data-mp="2t60vesuv1gqd">
                  Enquire
                </Link>
              </motion.div>)}
          </CardCarousel>
        </div>
      </section>

      {/* Corporate teaser → standalone page */}
      <section className="w-full bg-coffee py-20 text-paper md:py-28" data-id="element-457" data-mp="hfez9ipew8bn">
        <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-6 lg:grid-cols-[1.1fr_1fr] lg:gap-20" data-id="element-458" data-mp="hhrqkmh27v1d">
          <div data-id="element-459" data-mp="kxm72ogj3ivk">
            <p className="type-eyebrow text-[11px] text-paper/55" data-id="element-460" data-mp="kx318645kkxw">
              Corporate gifting &amp; custom branding
            </p>
            <h2 className="type-display mt-5 text-4xl text-paper md:text-5xl" data-id="element-461" data-mp="ozzfxlqknwwk">
              Buying for a whole company?
            </h2>
            <p className="type-body mt-6 max-w-md text-base text-paper/70" data-id="element-462" data-mp="b1rlbkh0d7ok">
              Custom sleeves, printing on solid colour cups, or a fully bespoke
              cup with your branding. From 50 units, with pricing, lead times
              and an enquiry form on one page.
            </p>
            <Link to="/corporate-branding" className="type-cta mt-9 inline-flex items-center gap-2 rounded-full bg-paper px-9 py-4 text-[11px] text-coffee transition-colors hover:bg-oat" data-id="element-463" data-mp="18ozzeueojegj">
              Corporate &amp; branded cups
              <ArrowRightIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-464" data-mp="1mldfq07h99tf" />
            </Link>
          </div>

          <div className="overflow-hidden rounded-4xl" data-id="element-465" data-mp="1rezohhaistjy">
            <img src="https://images.unsplash.com/photo-1445116572660-236099ec97a0?auto=format&fit=crop&q=80&w=1200" alt="Branded Ecoffee Cups on a café counter" className="aspect-[4/3] w-full object-cover" data-id="element-466" data-mp="219x39p8arv84" />
          </div>
        </div>
      </section>
    </div>;
}